// src/Components/Modules/HR/SalaryIncrement.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Tooltip, Skeleton, Alert, Stack, Avatar, TextField, Button
} from "@mui/material";
import {
  FaMoneyBillWave, FaCheckCircle, FaTimesCircle, FaClock, FaInfoCircle, FaPlus
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const FormCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  boxShadow: "0 8px 30px rgba(0,0,0,0.1)",
  overflow: "hidden",
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": { backgroundColor: theme.palette.action.hover },
  transition: "background-color 0.2s ease",
}));

const SalaryIncrement = () => {
  const role = parseInt(localStorage.getItem("who_am_i"), 10);
  const isAdmin = role <= 2;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ requested_salary: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("hr/salary-increment");
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch requests", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.requested_salary || !form.reason) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please fill all fields." });
      return;
    }

    const result = await Swal.fire({
      title: "Submit Request?",
      text: `You are requesting a salary of ৳${Number(form.requested_salary).toLocaleString()}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#667eea",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Submit",
    });

    if (result.isConfirmed) {
      setSubmitting(true);
      try {
        await AxiosAuthInstance.post("hr/salary-increment", form);
        Swal.fire({ icon: "success", title: "Success!", text: "Request submitted.", timer: 2000, showConfirmButton: false });
        setForm({ requested_salary: "", reason: "" });
        fetchRequests();
      } catch (err) {
        const msg = err.response?.data?.message || "Failed to submit.";
        Swal.fire({ icon: "error", title: "Error", text: msg });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleStatusUpdate = async (id, status) => {
    let rejection_reason = "";
    if (status === "reject") {
      const { value } = await Swal.fire({
        title: "Rejection Reason",
        input: "textarea",
        inputPlaceholder: "Why are you rejecting this?",
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        inputValidator: (value) => !value && "Reason is required!",
      });
      if (!value) return;
      rejection_reason = value;
    } else {
      const { isConfirmed } = await Swal.fire({
        title: "Approve Request?",
        text: "This will update the employee's salary immediately.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#4caf50",
      });
      if (!isConfirmed) return;
    }

    try {
      await AxiosAuthInstance.post(`hr/salary-increment/${id}/status`, { status, rejection_reason });
      Swal.fire({ icon: "success", title: "Success!", text: `Request ${status}d.`, timer: 2000, showConfirmButton: false });
      fetchRequests();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.response?.data?.message || "Action failed." });
    }
  };

  const getStatusChip = (status) => {
    if (status === "pending") return <Chip icon={<FaClock />} label="Pending" size="small" sx={{ bgcolor: "#fff3e0", color: "#ef6c00" }} />;
    if (status === "approved") return <Chip icon={<FaCheckCircle />} label="Approved" size="small" sx={{ bgcolor: "#e8f5e9", color: "#2e7d32" }} />;
    if (status === "rejected") return <Chip icon={<FaTimesCircle />} label="Rejected" size="small" sx={{ bgcolor: "#ffebee", color: "#c62828" }} />;
    return <Chip label={status} size="small" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Salary Increment Requests" />
      
      <GradientPaper elevation={0}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          <FaMoneyBillWave style={{ marginRight: 10 }} /> Salary Increment Management
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.9 }}>
          {isAdmin ? "Review and manage employee salary increment requests." : "Request a salary increment from your admin."}
        </Typography>
      </GradientPaper>

      {/* Employee Form */}
      {!isAdmin && (
        <FormCard sx={{ mb: 3 }}>
          <Box sx={{ p: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
            <Typography variant="h6" fontWeight={600}><FaPlus style={{ marginRight: 8 }} /> New Request</Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Requested Salary (BDT)" type="number" value={form.requested_salary} onChange={(e) => setForm({ ...form, requested_salary: e.target.value })} placeholder="e.g. 35000" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Reason for Increment" multiline rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Explain why you deserve an increment..." sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: 2, px: 4, py: 1.5, fontWeight: 600 }}>
                  {submitting ? "Submitting..." : "Submit Request"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </FormCard>
      )}

      {/* Requests Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell>Employee</StyledTableCell>
              <StyledTableCell align="right">Current Salary</StyledTableCell>
              <StyledTableCell align="right">Requested</StyledTableCell>
              <StyledTableCell align="right">Approved</StyledTableCell>
              <StyledTableCell>Reason</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              {isAdmin && <StyledTableCell align="center">Actions</StyledTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 8 : 7}><Skeleton height={60} /></TableCell></TableRow>
            ) : requests.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 8 : 7} align="center" sx={{ py: 6 }}><FaInfoCircle style={{ fontSize: 32, color: "#bdbdbd" }} /><Typography color="text.secondary" sx={{ mt: 1 }}>No requests found</Typography></TableCell></TableRow>
            ) : (
              requests.map((row) => (
                <StyledTableRow key={row.id} hover>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar sx={{ bgcolor: "#667eea", width: 32, height: 32 }}>{row.employee?.employee_name?.charAt(0) || "E"}</Avatar>
                      <Typography variant="body2" fontWeight={600}>{row.employee?.employee_name || "N/A"}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">৳{Number(row.current_salary).toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: "#1976d2", fontWeight: 600 }}>৳{Number(row.requested_salary).toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ color: row.approved_salary ? "#2e7d32" : "text.disabled", fontWeight: 600 }}>
                    {row.approved_salary ? `৳${Number(row.approved_salary).toLocaleString()}` : "-"}
                  </TableCell>
                  <TableCell><Typography variant="body2" sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.reason}</Typography></TableCell>
                  <TableCell><Typography variant="body2" color="text.secondary">{formatDate(row.created_at)}</Typography></TableCell>
                  <TableCell>{getStatusChip(row.status)}</TableCell>
                  {isAdmin && (
                    <TableCell align="center">
                      {row.status === "pending" && (
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="Approve"><IconButton size="small" onClick={() => handleStatusUpdate(row.id, "approve")} sx={{ color: "#4caf50" }}><FaCheckCircle /></IconButton></Tooltip>
                          <Tooltip title="Reject"><IconButton size="small" onClick={() => handleStatusUpdate(row.id, "reject")} sx={{ color: "#f44336" }}><FaTimesCircle /></IconButton></Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  )}
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </Box>
  );
};

export default SalaryIncrement;